import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const sections = [
  {
    id: 'datos',
    icon: 'ri-database-2-line',
    title: '1. Datos recopilados',
    content: `Se podrán recabar datos como nombre, correo electrónico, teléfono, ubicación y datos necesarios para la prestación del servicio.`,
  },
  {
    id: 'finalidad',
    icon: 'ri-focus-3-line',
    title: '2. Finalidad del tratamiento',
    content: `Los datos serán utilizados para:\n• Facilitar la conexión entre usuarios y proveedores\n• Gestionar solicitudes y servicios\n• Mejorar la experiencia dentro de la plataforma\n• Brindar soporte y atención`,
  },
  {
    id: 'proteccion',
    icon: 'ri-shield-check-line',
    title: '3. Protección de datos',
    content: `Task Zone implementa medidas de seguridad administrativas, técnicas y físicas para proteger la información personal.`,
  },
  {
    id: 'confidencialidad',
    icon: 'ri-lock-line',
    title: '4. Confidencialidad',
    content: `Los datos no serán compartidos con terceros sin consentimiento, salvo obligación legal.`,
  },
  {
    id: 'monitoreo',
    icon: 'ri-chat-private-line',
    title: '5. Monitoreo de comunicaciones',
    content: `Las conversaciones dentro de la plataforma podrán ser revisadas con fines de seguridad, cumplimiento de normas y mejora del servicio.`,
  },
  {
    id: 'derechos',
    icon: 'ri-user-heart-line',
    title: '6. Derechos ARCO',
    content: `Los usuarios podrán ejercer sus derechos de acceso, rectificación, cancelación y oposición respecto a sus datos personales.`,
  },
  {
    id: 'ubicacion',
    icon: 'ri-map-pin-line',
    title: '7. Uso de ubicación',
    content: `La información de ubicación será utilizada exclusivamente para la prestación del servicio y no será visible públicamente.`,
  },
  {
    id: 'modificaciones',
    icon: 'ri-refresh-line',
    title: '8. Modificaciones',
    content: `Task Zone podrá actualizar esta política en cualquier momento.`,
  },
];

const PrivacyPage = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Política de Privacidad | Task Zone';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Política de privacidad de Task Zone conforme a la LFPDPPP. Conoce cómo protegemos tus datos personales, tus derechos ARCO y el uso de información en nuestra plataforma de servicios en Aguascalientes.');

    const schemaId = 'schema-privacy';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Política de Privacidad – Task Zone',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/politica-de-privacidad`,
        description: 'Política de privacidad de Task Zone conforme a la LFPDPPP. Protección de datos personales de usuarios y proveedores en Aguascalientes.',
        dateModified: '2026-04-07',
        publisher: {
          '@type': 'Organization',
          name: 'Task Zone',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const toggleSection = (id: string) => {
    setActiveSection(activeSection === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <section className="pt-16 bg-gradient-to-br from-gray-800 to-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-orange-500/20 border border-orange-500/30 rounded-2xl mx-auto mb-5">
            <i className="ri-shield-user-line text-orange-400 text-3xl" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Política de Privacidad</h1>
          <p className="text-gray-400 text-sm max-w-xl mx-auto mb-4">
            Última actualización: 7 de abril de 2026
          </p>
          <p className="text-gray-400 text-sm max-w-2xl mx-auto">
            Task Zone se compromete a proteger los datos personales de sus usuarios y proveedores conforme a la legislación mexicana aplicable, en particular la Ley Federal de Protección de Datos Personales en Posesión de los Particulares.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {['Datos seguros', 'Sin venta a terceros', 'Derechos ARCO', 'Cumplimiento LFPDPPP'].map((badge) => (
              <span key={badge} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-gray-300 text-xs px-3 py-1.5 rounded-full">
                <i className="ri-check-line text-orange-400" /> {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 bg-gray-50 border border-gray-100 rounded-xl p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Secciones</p>
              <ul className="space-y-1">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={() => setActiveSection(s.id)}
                      className="flex items-center gap-2 text-xs text-gray-600 hover:text-orange-600 py-1.5 px-2 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer"
                    >
                      <i className={`${s.icon} text-orange-400 flex-shrink-0`} />
                      <span className="leading-tight">{s.title.replace(/^\d+\.\s/, '')}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main */}
          <div className="flex-1 space-y-4">

            {/* Intro notice */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-lg flex-shrink-0">
                <i className="ri-information-line text-white text-lg" />
              </div>
              <div>
                <p className="text-gray-800 font-semibold text-sm mb-1">Tu privacidad es nuestra prioridad</p>
                <p className="text-gray-600 text-xs leading-relaxed">
                  Task Zone nunca muestra públicamente tu teléfono, correo electrónico ni dirección personal. Toda la comunicación ocurre a través del sistema interno de mensajería de la plataforma.
                </p>
              </div>
            </div>

            {sections.map((s) => (
              <div
                key={s.id}
                id={s.id}
                className="border border-gray-100 rounded-xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleSection(s.id)}
                  className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-orange-50 transition-colors cursor-pointer text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                      <i className={`${s.icon} text-orange-500 text-sm`} />
                    </div>
                    <span className="font-semibold text-gray-800 text-sm">{s.title}</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${activeSection === s.id ? 'rotate-180' : ''}`} />
                </button>
                {activeSection === s.id && (
                  <div className="px-6 py-5 bg-white border-t border-gray-100">
                    {s.content.split('\n').map((line, i) => (
                      line.trim() === '' ? <br key={i} /> : (
                        <p key={i} className="text-gray-600 text-sm leading-relaxed mb-1">{line}</p>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Contact box */}
            <div className="bg-gray-800 rounded-xl p-6 mt-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-lg flex-shrink-0">
                  <i className="ri-mail-send-line text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm mb-1">Contacto para asuntos de privacidad</h3>
                  <p className="text-gray-400 text-xs leading-relaxed mb-3">
                    Para ejercer tus derechos ARCO o cualquier consulta sobre el tratamiento de tus datos personales, contáctanos directamente.
                  </p>
                  <a href="mailto:taskzone.app@gmail.com" className="inline-flex items-center gap-2 text-orange-400 text-xs font-semibold hover:underline">
                    <i className="ri-mail-line" /> taskzone.app@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section className="bg-gray-50 border-t border-gray-100 py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">También puede interesarte:</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/terminos-y-condiciones"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-xs font-medium px-4 py-2 rounded-lg hover:border-orange-300 hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-list-3-line text-orange-500" /> Términos y Condiciones
            </Link>
            <Link
              to="/sobre-task-zone"
              className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 text-xs font-medium px-4 py-2 rounded-lg hover:border-orange-300 hover:text-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line text-orange-500" /> Sobre Task Zone!
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
